import React, { lazy, Suspense, useEffect, useState, useTransition } from "react";
import {
    addParcelFormSteps,
    addParcelInitialValues,
    ResponsiveFormConfig,
    updatedParcelSuccessToast,
} from "../create-property/definitions";
import Column from "@amzn/meridian/column";
import { parcelDataExtractKeys } from "../create-property/utils";
import _ from "lodash";
import ApiIndicator from "../../features/api-indicator/ApiIndicator";
import { useGetArtifactDetailsByIdQuery } from "../../endpoints/getArtifactDetailsById";
import { parseJSON } from "../../utils/helperUtils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Row from "@amzn/meridian/row";
import Box from "@amzn/meridian/box";
import { useUpdateArtifactDetailsByIdMutation } from "../../endpoints/updateArtifactDetailsById";
import { useAppSelector } from "../../app/store/hooks";
import { useNotifications } from "../../features/notifications/hooks";
import { useCreateArtifactMutation } from "../../endpoints/createArtifact";

const LazyAddParcelMultiStepForm = lazy(
    () => import("../create-property/ResponsiveProgressTrackerForm")
);

export const AddEditParcel: React.FC<any> = () => {
    const { alias } = useAppSelector(state => state.auth.user);
    const [initialValues, setInitialValues] = useState(null);
    const { parcelId } = useParams();
    const { pathname } = useLocation();
    const location = useLocation();
    const [onAddParcel] = useState(pathname.split("/").splice(-1)[0] === "add-parcel");
    const [onEditParcel] = useState(pathname.split("/").splice(-1)[0] === "edit-parcel");
    const [propertyNumber] = useState(location.state?.propertyNumber);
    const [parcelNumber, setParcelNumber] = useState("");
    const navigate = useNavigate();

    const { data, isSuccess } = useGetArtifactDetailsByIdQuery(
        {
            artifactType: "PARCEL",
            artifactId: parcelId,
            businessUseCase: "SMART",
        },
        { skip: onAddParcel }
    );

    const [
        updateParcelTrigger,
        {
            error: updateError,
            isLoading: updateIsLoading,
            isSuccess: updateIsSuccess,
            isError: updateIsError,
        },
    ] = useUpdateArtifactDetailsByIdMutation();

    const [
        createParcelTrigger,
        {
            error: createError,
            isLoading: createIsLoading,
            isSuccess: createSuccess,
            isError: createIsError,
        },
    ] = useCreateArtifactMutation();

    const [, startTransition] = useTransition();

    useEffect(() => {
        startTransition(() => {
            if (!onAddParcel && isSuccess) {
                const parsedData = parseJSON(data.artifactDetails);
                const dataKeys = parcelDataExtractKeys;

                const result = {
                    ..._.pick(parsedData, dataKeys.parcelNumber),
                    ..._.pick(parsedData, dataKeys.siteCode),
                    ..._.pick(parsedData, dataKeys.address),
                    ..._.pick(parsedData, dataKeys.basicInformation),
                    ..._.pick(parsedData, dataKeys.statusAndOwnership),
                    ..._.pick(parsedData, dataKeys.otherInformation),
                    ..._.pick(parsedData, dataKeys.financialInformation),
                };

                _.pickBy(data.artifactDetails, value => {
                    if (result.parcelNumber === value) {
                        return true;
                    }

                    return true;
                });
                startTransition(() => {
                    setInitialValues(result);
                });
                ``;
            }
        });
    }, [data, isSuccess]);
    const { addToast } = useNotifications();

    useEffect(() => {
        if (updateIsSuccess) {
            updatedParcelSuccessToast(parcelNumber, addToast);
            navigate(-1);
        }
    }, [updateIsSuccess]);
    useEffect(() => {
        if (createSuccess) {
            updatedParcelSuccessToast(parcelNumber, addToast);
            navigate(-1);
        }
    }, [createSuccess]);

    const handleSubmit = async newValues => {
        setParcelNumber(newValues[0].parcelNumber);

        if (onAddParcel) {
            await createParcelTrigger({
                businessUseCase: "SMART",
                artifactType: "PARCEL",
                clientName: alias,
                artifactDetails: {
                    propertyNumber,
                    ...newValues[0],
                },
            });
        }

        if (onEditParcel) {
            await updateParcelTrigger({
                clientName: alias,
                updatedArtifactDetails: {
                    ...data.artifactDetails,
                    ...newValues[0],
                },
                artifactType: data.artifactType,
                artifactId: data.artifactId,
                businessUseCase: "SMART",
                updatedBy: alias,
            });
        }
    };

    return (
        <Column>
            <ApiIndicator
                isLoading={updateIsLoading || createIsLoading}
                isError={updateIsError || createIsError}
                error={updateError || createError}
                isToastError={true}
                loaderType={"linear"}
                timeout={10000}
            />
            {initialValues || onAddParcel ? (
                <Suspense
                    fallback={
                        <Row height={"primary"} width={"100%"}>
                            <Box height={"100%"}></Box>
                        </Row>
                    }
                >
                    <LazyAddParcelMultiStepForm
                        responsiveConfig={ResponsiveFormConfig}
                        forms={addParcelFormSteps}
                        formName={"editProperty"}
                        formInitialValues={initialValues || addParcelInitialValues}
                        onFinalFormSubmit={handleSubmit}
                    />
                </Suspense>
            ) : null}
        </Column>
    );
};
